import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Eye, EyeOff } from "lucide-react";
import { authService } from "@/services/authService";
import { getApiErrorMessage } from "@/utils/apiErrors";

const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MIN_LENGTH_MESSAGE = "Password must be at least 8 characters.";
const PASSWORD_SPECIAL_CHAR_MESSAGE =
  "Invalid password: must contain at least 1 special characters.";
const EMAIL_INVALID_MESSAGE = "Please enter a valid email address.";
const COMPANY_NAME_MAX_LENGTH = 100;
const COMPANY_NAME_MAX_LENGTH_MESSAGE = `Company name must be at most ${COMPANY_NAME_MAX_LENGTH} characters.`;

function validateEmail(email: string): string | null {
  const trimmed = email.trim();
  if (!trimmed) return "Email is required.";

  if (!trimmed.includes("@")) {
    return `Please include an '@' in the email address. '${trimmed}' is missing an '@'.`;
  }

  const atIndex = trimmed.indexOf("@");
  const localPart = trimmed.slice(0, atIndex);
  const domainPart = trimmed.slice(atIndex + 1);

  if (!localPart) {
    return `Please enter a part followed by '@'. '${trimmed}' is incomplete.`;
  }

  if (!domainPart || !domainPart.includes(".")) {
    return EMAIL_INVALID_MESSAGE;
  }

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
    return EMAIL_INVALID_MESSAGE;
  }

  return null;
}

function passwordHasSpecialChar(password: string): boolean {
  return /[^A-Za-z0-9]/.test(password);
}

function validatePassword(password: string): string | null {
  if (password.length < PASSWORD_MIN_LENGTH) return PASSWORD_MIN_LENGTH_MESSAGE;
  if (!passwordHasSpecialChar(password)) return PASSWORD_SPECIAL_CHAR_MESSAGE;
  return null;
}

type PasswordStrengthLevel = {
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  fillClass: string;
};

function getPasswordStrength(password: string): PasswordStrengthLevel {
  if (!password) {
    return { score: 0, label: "", fillClass: "bg-gray-200" };
  }

  let score = 0;
  if (password.length >= 4) score++;
  if (password.length >= PASSWORD_MIN_LENGTH) score++;
  if (passwordHasSpecialChar(password)) score++;
  if (
    (/[a-z]/.test(password) && /[A-Z]/.test(password)) ||
    (/\d/.test(password) && password.length >= 10)
  ) {
    score++;
  }

  const level = Math.min(score, 4) as 1 | 2 | 3 | 4;
  if (level === 1) return { score: 1, label: "Weak", fillClass: "bg-red-500" };
  if (level === 2) return { score: 2, label: "Fair", fillClass: "bg-orange-400" };
  if (level === 3) return { score: 3, label: "Good", fillClass: "bg-amber-500" };
  return { score: 4, label: "Strong", fillClass: "bg-emerald-800" };
}

export const SignupPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyNameError, setCompanyNameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    companyName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const handleCompanyNameChange = (companyName: string) => {
    setFormData({ ...formData, companyName });
    setCompanyNameError(
      companyName.length > COMPANY_NAME_MAX_LENGTH ? COMPANY_NAME_MAX_LENGTH_MESSAGE : null,
    );
  };

  const handleEmailChange = (email: string) => {
    setFormData({ ...formData, email });
    if (email.trim().length === 0) {
      setEmailError(null);
      return;
    }
    setEmailError(validateEmail(email));
  };

  const handlePasswordChange = (password: string) => {
    setFormData({ ...formData, password });
    if (password.length === 0) {
      setPasswordError(null);
      return;
    }
    setPasswordError(validatePassword(password));
  };

  const emailIsValid =
    formData.email.trim().length > 0 && validateEmail(formData.email) === null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (formData.companyName.length > COMPANY_NAME_MAX_LENGTH) {
      setCompanyNameError(COMPANY_NAME_MAX_LENGTH_MESSAGE);
      setError(COMPANY_NAME_MAX_LENGTH_MESSAGE);
      return;
    }

    const emailValidationError = validateEmail(formData.email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      setError(emailValidationError);
      return;
    }

    const passwordValidationError = validatePassword(formData.password);
    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      setError(passwordValidationError);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await authService.signup({
        companyName: formData.companyName,
        email: formData.email.trim(),
        password: formData.password,
      });
      setSuccess(true);
    } catch (err: unknown) {
      const message = getApiErrorMessage(err) ?? "Signup failed. Please try again.";
      setError(message);
      if (message.toLowerCase().includes("email")) {
        setEmailError(message);
      }
      if (
        message === PASSWORD_SPECIAL_CHAR_MESSAGE ||
        message === PASSWORD_MIN_LENGTH_MESSAGE
      ) {
        setPasswordError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center">
        <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-bridge-text mb-2">Registration Submitted</h2>
        <p className="text-bridge-muted mb-6">
          Thank you for signing up! Please connect with our support team to complete your registration.
        </p>
        <Link
          to="/auth/login"
          className="inline-block px-6 py-2 bg-bridge-accent text-white rounded-xl font-medium hover:opacity-90 transition-opacity"
        >
          Go to Login
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Create your Account</h2>
        <p className="text-sm text-gray-500">Join BRIDGE AI to manage your leads seamlessly.</p>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl">
          {error}
        </div>
      )}

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-1.5">
          Company Name <span className="text-gray-900">*</span>
        </label>
        <input
          type="text"
          required
          value={formData.companyName}
          onChange={(e) => handleCompanyNameChange(e.target.value)}
          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00a3ff]/20 focus:border-[#00a3ff] outline-none transition-all placeholder:text-gray-400"
          placeholder="Company Name"
        />
        {companyNameError && (
          <p className="mt-1.5 text-sm text-red-600">{companyNameError}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-1.5">
          Email <span className="text-gray-900">*</span>
        </label>
        <div className="relative">
          <input
            type="text"
            inputMode="email"
            autoComplete="email"
            value={formData.email}
            onChange={(e) => handleEmailChange(e.target.value)}
            className={`w-full py-3 pl-4 bg-white border rounded-xl focus:ring-2 focus:ring-[#00a3ff]/20 focus:border-[#00a3ff] outline-none transition-all placeholder:text-gray-400 ${
              emailError ? "border-red-300 pr-4" : `border-gray-200 ${emailIsValid ? "pr-11" : "pr-4"}`
            }`}
            placeholder="Email"
            aria-invalid={emailError ? true : undefined}
            aria-describedby={emailError ? "email-error" : "email-hint"}
          />
          {emailIsValid ? (
            <span
              className="absolute inset-y-0 right-3 flex items-center text-emerald-600"
              aria-hidden
            >
              <Check size={18} strokeWidth={2.5} />
            </span>
          ) : null}
        </div>
        {emailError ? (
          <p id="email-error" className="mt-1.5 text-sm text-red-600">
            {emailError}
          </p>
        ) : !formData.email.trim() ? (
          <p id="email-hint" className="mt-1.5 text-xs text-gray-500">
            Enter a valid email address (e.g. name@company.com).
          </p>
        ) : null}
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-1.5">
          Password <span className="text-gray-900">*</span>
        </label>
        <PasswordField
          required
          value={formData.password}
          onChange={handlePasswordChange}
          placeholder="Password"
          hasError={Boolean(passwordError)}
          aria-invalid={passwordError ? true : undefined}
          aria-describedby={
            passwordError ? "password-error" : formData.password ? "password-strength" : undefined
          }
        />
        {formData.password ? (
          <PasswordStrengthIndicator password={formData.password} />
        ) : null}
        {passwordError ? (
          <p id="password-error" className="mt-1.5 text-sm text-red-600">
            {passwordError}
          </p>
        ) : (
          <p className="mt-1.5 text-xs text-gray-500">
            Must be at least 8 characters and contain 1 special character (e.g. ! @ # $).
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-1.5">
          Confirm password <span className="text-gray-900">*</span>
        </label>
        <PasswordField
          required
          value={formData.confirmPassword}
          onChange={(value) => setFormData({ ...formData, confirmPassword: value })}
          placeholder="Confirm password"
        />
      </div>

      <div className="flex items-center pt-2 pb-4">
        <input
          type="checkbox"
          id="terms"
          required
          className="w-4 h-4 text-[#00a3ff] border-gray-300 rounded focus:ring-[#00a3ff]"
        />
        <label htmlFor="terms" className="ml-2 text-sm text-gray-700">
          Accept <a href="#" className="text-[#00a3ff] hover:underline">Terms & Conditions</a>
        </label>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-3 bg-[#00a3ff] text-white rounded-xl font-semibold shadow-md shadow-[#00a3ff]/20 hover:bg-[#0091e6] disabled:opacity-50 transition-all"
      >
        {loading ? "Registering..." : "Register"}
      </button>

      <div className="text-center text-sm text-gray-600 pt-4">
        Already have an account?{" "}
        <Link to="/auth/login" className="text-[#00a3ff] font-semibold hover:underline">
          Login
        </Link>
      </div>
    </form>
  );
};

function PasswordStrengthIndicator({ password }: { password: string }) {
  const { score, label, fillClass } = getPasswordStrength(password);

  return (
    <div id="password-strength" className="mt-2">
      <div className="flex gap-1" aria-hidden>
        {Array.from({ length: 4 }, (_, index) => (
          <div
            key={index}
            className={`h-1 flex-1 rounded-full transition-colors duration-200 ${
              index < score ? fillClass : "bg-gray-200"
            }`}
          />
        ))}
      </div>
      <p className="mt-2 text-sm">
        <span className="text-gray-500">Strength: </span>
        <span className="font-semibold text-gray-900">{label}</span>
      </p>
    </div>
  );
}

function PasswordField({
  value,
  onChange,
  placeholder,
  required,
  hasError,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  required?: boolean;
  hasError?: boolean;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
}) {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="relative">
      <input
        type={showPassword ? "text" : "password"}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full py-3 pl-4 pr-11 bg-white border rounded-xl focus:ring-2 focus:ring-[#00a3ff]/20 focus:border-[#00a3ff] outline-none transition-all placeholder:text-gray-400 ${
          hasError ? "border-red-300" : "border-gray-200"
        }`}
        placeholder={placeholder}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
      />
      <button
        type="button"
        onClick={() => setShowPassword((visible) => !visible)}
        aria-label={showPassword ? "Hide password" : "Show password"}
        className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
      >
        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
}
